/**
 * Jun 2026 · Sales Order Reconciliation (DOMESTIC SO ONLY)
 *
 * Single-SO, read-only item & dispatch-document traceability:
 *   SO Item → Packing Slip → Packing List → Delivered DC → GST Invoice.
 *
 * SEPARATE from the legacy cross-order SO+ESO aggregate grid
 * (SalesOrderReconciliation.jsx / GET /reconciliation/sales-orders),
 * which is untouched. SO-only at every layer — no ESO/EDC/EPI data.
 * Read-only: no edit / cancel / dispatch / invoice actions.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  ArrowsClockwiseIcon, MagnifyingGlassIcon, PackageIcon, TruckIcon,
  WarningCircleIcon, CheckCircleIcon, CaretDownIcon, CaretRightIcon,
} from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const STATUS_STYLE = {
  "Pending": "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  "Part Dispatch": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Completed": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Hold": "bg-orange-500/15 text-orange-300 border-orange-500/30",
  "Short Closed": "bg-purple-500/15 text-purple-300 border-purple-500/30",
  "Cancelled": "bg-red-500/15 text-red-300 border-red-500/30",
};

const PsCell = ({ list }) => {
  if (!list || list.length === 0) return <span className="text-zinc-600">—</span>;
  if (list.length === 1) return <span className="font-mono text-xs text-zinc-300">{list[0]}</span>;
  return (
    <span className="font-mono text-xs text-zinc-300" title={list.join(", ")}>
      {list[0]} <span className="text-zinc-500">+{list.length - 1}</span>
    </span>
  );
};

export default function DomesticSalesOrderReconciliation() {
  const [orders, setOrders] = useState([]);
  const [selId, setSelId] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("items");
  const [openItem, setOpenItem] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reconciliation/domestic-sales-orders");
        setOrders(data?.orders || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(o =>
      (o.order_no || "").toLowerCase().includes(q) ||
      (o.customer || "").toLowerCase().includes(q) ||
      (o.po_number || "").toLowerCase().includes(q));
  }, [orders, query]);

  const load = async (id) => {
    const oid = id || selId;
    if (!oid) return;
    setLoading(true); setOpenItem(null); setTab("items");
    try {
      const { data } = await api.get(`/reconciliation/domestic-sales-order/${oid}`);
      setData(data);
    } finally { setLoading(false); }
  };

  const onPickOrderNo = (val) => {
    setQuery(val);
    const hit = orders.find(o => o.order_no === val);
    if (hit) { setSelId(hit.id); load(hit.id); }
  };

  const order = data?.order;
  const items = data?.items || [];
  const dispatchDocs = data?.dispatch_documents || [];
  const summary = data?.summary || {};
  const exceptions = data?.exceptions || [];

  return (
    <div className="p-6 space-y-5" data-testid="dso-recon-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-zinc-500 mb-1">Reports · Domestic</div>
          <h1 className="font-display text-2xl">Sales Order Reconciliation</h1>
          <p className="text-xs text-zinc-400 mt-1">Item-wise Order, Dispatch &amp; Document Traceability — Domestic Sales Orders only.</p>
        </div>
        {order && (
          <button data-testid="dso-recon-refresh" onClick={() => load()} disabled={loading}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
          </button>
        )}
      </div>

      {/* SO selector */}
      <div className="tactical-card p-3 flex items-end gap-3 flex-wrap" data-testid="dso-recon-selector">
        <div className="min-w-[340px] flex-1">
          <div className="overline text-[10px] mb-1">Sales Order No.</div>
          <div className="relative">
            <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="dso-recon-so-input" list="dso-recon-orders" value={query}
              onChange={e => onPickOrderNo(e.target.value)}
              placeholder="Search SO No / Customer / Customer PO…"
              className="w-full h-10 pl-7 pr-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono focus:outline-none focus:border-[var(--accent)]" />
            <datalist id="dso-recon-orders">
              {matched.map(o => <option key={o.id} value={o.order_no}>{o.customer} · {o.status}</option>)}
            </datalist>
          </div>
        </div>
        <button data-testid="dso-recon-load" onClick={() => load()} disabled={!selId || loading}
          className="h-10 px-5 bg-[var(--accent)] hover:bg-blue-600 disabled:opacity-40 text-white text-sm font-semibold rounded-sm">
          {loading ? "Loading…" : "Load Reconciliation"}
        </button>
        <div className="ml-auto text-[11px] text-zinc-500 font-mono">{orders.length} domestic SOs</div>
      </div>

      {!order && !loading && (
        <div className="tactical-card p-12 text-center text-zinc-500 text-sm" data-testid="dso-recon-empty">
          Select a Domestic Sales Order to reconcile its items &amp; dispatch documents.
        </div>
      )}

      {order && (
        <>
          {/* SO header card */}
          <div className="tactical-card p-4" data-testid="dso-recon-header">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {[
                ["SO No.", order.order_no, "font-mono text-[var(--accent)]"],
                ["SO Date", order.order_date || "—", "font-mono text-zinc-300"],
                ["Customer", order.customer || "—", "text-zinc-200"],
                ["Customer PO", order.po_number || "—", "font-mono text-zinc-300"],
                ["Delivery Date", order.delivery_date || "—", "font-mono text-zinc-300"],
                ["Lifecycle", order.status || "—", "text-zinc-200"],
                ["Authorization", order.authorized ? "Authorised" : "Not Authorised", order.authorized ? "text-emerald-300" : "text-zinc-400"],
              ].map(([label, val, cls]) => (
                <div key={label}>
                  <div className="overline text-[10px] text-zinc-500 mb-1">{label}</div>
                  <div className={`text-sm font-medium leading-tight ${cls}`} title={String(val)}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Exceptions banner */}
          {exceptions.length > 0 && (
            <div className="tactical-card p-3 border-l-2 border-amber-500/60 bg-amber-500/5" data-testid="dso-recon-exceptions">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold mb-1">
                <WarningCircleIcon size={14} weight="bold" /> {exceptions.length} reconciliation exception(s)
              </div>
              <ul className="text-[11px] text-zinc-400 space-y-0.5 font-mono">
                {exceptions.map((ex, i) => <li key={i}>• Item {ex.item_no}: {ex.type} — {ex.detail}</li>)}
              </ul>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-[var(--border)]">
            {[["items", "Item Reconciliation", PackageIcon], ["dispatch", "Dispatch Documents", TruckIcon]].map(([k, label, Icon]) => (
              <button key={k} data-testid={`dso-recon-tab-${k}`} onClick={() => setTab(k)}
                className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === k ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
                <Icon size={14} weight="bold" />{label}
              </button>
            ))}
          </div>

          {/* TAB 1 — ITEM RECONCILIATION */}
          {tab === "items" && (
            <div className="tactical-card overflow-x-auto" data-testid="dso-recon-items">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                    <th className="text-left px-3 py-3 w-8"></th>
                    <th className="text-left px-3 py-3">Item No</th>
                    <th className="text-left px-3 py-3">Style No</th>
                    <th className="text-left px-3 py-3">Product Code</th>
                    <th className="text-right px-3 py-3">Order Qty</th>
                    <th className="text-right px-3 py-3">Buffer</th>
                    <th className="text-right px-3 py-3">Dispatch</th>
                    <th className="text-right px-3 py-3">Pending</th>
                    <th className="text-right px-3 py-3">Hold</th>
                    <th className="text-right px-3 py-3">Short Closed</th>
                    <th className="text-right px-3 py-3">Cancelled</th>
                    <th className="text-left px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={12} className="text-center py-12 text-zinc-500">No SO lines.</td></tr>
                  )}
                  {items.map((it) => {
                    const isOpen = openItem === it.item_no;
                    const rec = it.reconciliation || {};
                    return (
                      <>
                        <tr key={it.item_no} data-testid={`dso-recon-item-${it.item_no}`}
                          onClick={() => setOpenItem(isOpen ? null : it.item_no)}
                          className={`border-b border-[var(--border)]/40 hover:bg-[var(--bg-2)]/40 cursor-pointer ${it.status === "Cancelled" ? "opacity-75" : ""}`}>
                          <td className="px-3 py-2 text-zinc-500">{isOpen ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}</td>
                          <td className="px-3 py-2 font-mono text-zinc-300 text-xs">{it.item_no}</td>
                          <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{it.style_no || "—"}</td>
                          <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs truncate max-w-[260px]" title={it.product_code}>{it.product_code || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-white tabular-nums">{fmt(it.order_qty)}<span className="text-zinc-500 ml-1 text-[10px]">{it.uom}</span></td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-400 tabular-nums">{fmt(it.buffer_qty)}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-300 tabular-nums">{fmt(it.dispatch_qty)}</td>
                          <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${it.pending_qty > 0 ? "text-amber-300" : "text-emerald-400"}`}>{fmt(it.pending_qty)}</td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-400 tabular-nums">{it.hold_qty > 0 ? fmt(it.hold_qty) : "—"}</td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-400 tabular-nums">{it.short_closed_qty > 0 ? fmt(it.short_closed_qty) : "—"}</td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-400 tabular-nums">{it.cancelled_qty > 0 ? fmt(it.cancelled_qty) : "—"}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[it.status] || STATUS_STYLE.Pending}`}>{it.status}</span>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr key={`${it.item_no}-detail`} data-testid={`dso-recon-item-detail-${it.item_no}`}>
                            <td colSpan={12} className="px-4 py-4 bg-[var(--bg-0)]/60 border-b border-[var(--border)]">
                              {/* Reconciliation control */}
                              <div className="flex flex-wrap gap-5 mb-4 text-xs">
                                {[
                                  ["Order Qty", it.order_qty], ["Cancelled", it.cancelled_qty],
                                  ["Short Closed", it.short_closed_qty], ["Net Requirement", rec.net_requirement],
                                  ["Delivered", it.dispatch_qty], ["Expected Balance", rec.expected_balance],
                                  ["Pending (auth)", rec.pending_qty], ["Variance", rec.variance],
                                ].map(([l, v]) => (
                                  <div key={l}>
                                    <div className="overline text-[9px] text-zinc-500">{l}</div>
                                    <div className={`font-mono tabular-nums ${l === "Variance" ? (Math.abs(Number(v)) < 0.01 ? "text-emerald-400" : "text-red-400 font-semibold") : "text-zinc-200"}`}>{fmt(v)}</div>
                                  </div>
                                ))}
                                <div className="ml-auto flex items-center">
                                  {rec.reconciled
                                    ? <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs font-semibold"><CheckCircleIcon size={14} weight="fill" />RECONCILED</span>
                                    : <span className="inline-flex items-center gap-1.5 text-red-300 text-xs font-semibold"><WarningCircleIcon size={14} weight="fill" />VARIANCE</span>}
                                </div>
                              </div>
                              {/* Item dispatch details */}
                              <div className="overline text-[10px] text-zinc-500 mb-2">Item Dispatch Details</div>
                              {it.dispatches.length === 0 ? (
                                <div className="text-zinc-500 text-xs py-3">No delivered dispatch for this item.</div>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                                      <th className="text-left px-2 py-1.5">Dispatch Date</th>
                                      <th className="text-left px-2 py-1.5">Packing Slip</th>
                                      <th className="text-left px-2 py-1.5">Packing List</th>
                                      <th className="text-left px-2 py-1.5">DC No</th>
                                      <th className="text-left px-2 py-1.5">GST Invoice</th>
                                      <th className="text-right px-2 py-1.5">Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {it.dispatches.map((d, di) => (
                                      <tr key={di} className="border-b border-[var(--border)]/30">
                                        <td className="px-2 py-1.5 font-mono text-zinc-300">{d.dispatch_date || "—"}</td>
                                        <td className="px-2 py-1.5"><PsCell list={d.ps_nos} /></td>
                                        <td className="px-2 py-1.5 font-mono text-zinc-300">{d.pl_no || "—"}</td>
                                        <td className="px-2 py-1.5 font-mono text-zinc-300">{d.dc_no || "—"}</td>
                                        <td className={`px-2 py-1.5 font-mono ${d.gst_invoice_no ? "text-sky-300" : "text-zinc-600"}`}>{d.gst_invoice_no || "—"}</td>
                                        <td className="px-2 py-1.5 text-right font-mono text-emerald-300 tabular-nums">{fmt(d.qty)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>

              {/* SO-level summary (per UOM) */}
              {(summary.by_uom || []).length > 0 && (
                <div className="border-t-2 border-[var(--accent)]/40 bg-[var(--bg-2)]/40 p-3" data-testid="dso-recon-summary">
                  <div className="flex items-center justify-between mb-2">
                    <div className="overline text-[10px] text-[var(--accent)]">SO Summary · {summary.lines} lines</div>
                    {summary.reconciled
                      ? <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs font-semibold"><CheckCircleIcon size={13} weight="fill" />RECONCILED</span>
                      : <span className="inline-flex items-center gap-1.5 text-red-300 text-xs font-semibold" data-testid="dso-recon-summary-variance"><WarningCircleIcon size={13} weight="fill" />VARIANCE {fmt(summary.variance)}</span>}
                  </div>
                  {summary.by_uom.map((g) => (
                    <div key={g.uom} className="flex flex-wrap gap-5 text-xs py-1">
                      <span className="font-mono text-zinc-500 uppercase w-16">{g.uom}</span>
                      {[["Order", g.order_qty], ["Buffer", g.buffer_qty], ["Dispatch", g.dispatch_qty], ["Pending", g.pending_qty], ["Short Closed", g.short_closed_qty], ["Cancelled", g.cancelled_qty]].map(([l, v]) => (
                        <span key={l} className="text-zinc-400">{l}: <span className="font-mono text-zinc-200 tabular-nums">{fmt(v)}</span></span>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2 — DISPATCH DOCUMENTS */}
          {tab === "dispatch" && (
            <div className="tactical-card overflow-x-auto" data-testid="dso-recon-dispatch">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                    <th className="text-left px-3 py-3">Dispatch Date</th>
                    <th className="text-left px-3 py-3">Item No</th>
                    <th className="text-left px-3 py-3">Style No</th>
                    <th className="text-left px-3 py-3">Product Code</th>
                    <th className="text-left px-3 py-3">Packing Slip</th>
                    <th className="text-left px-3 py-3">Packing List</th>
                    <th className="text-left px-3 py-3">DC No</th>
                    <th className="text-left px-3 py-3">GST Invoice</th>
                    <th className="text-right px-3 py-3">Dispatch Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatchDocs.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-12 text-zinc-500" data-testid="dso-recon-no-dispatch">No delivered dispatch yet for this Sales Order.</td></tr>
                  )}
                  {dispatchDocs.map((d, i) => (
                    <tr key={i} data-testid={`dso-recon-dispatch-row-${i}`} className="border-b border-[var(--border)]/40 hover:bg-[var(--bg-2)]/40">
                      <td className="px-3 py-2 font-mono text-zinc-300 text-xs">{d.dispatch_date || "—"}</td>
                      <td className="px-3 py-2 font-mono text-zinc-300 text-xs">{d.item_no}</td>
                      <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{d.style_no || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs truncate max-w-[240px]" title={d.product_code}>{d.product_code || "—"}</td>
                      <td className="px-3 py-2"><PsCell list={d.ps_nos} /></td>
                      <td className="px-3 py-2 font-mono text-zinc-300 text-xs">{d.pl_no || "—"}</td>
                      <td className="px-3 py-2 font-mono text-zinc-300 text-xs">{d.dc_no || "—"}</td>
                      <td className={`px-3 py-2 font-mono text-xs ${d.gst_invoice_no ? "text-sky-300" : "text-zinc-600"}`}>{d.gst_invoice_no || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-300 tabular-nums">{fmt(d.qty)}<span className="text-zinc-500 ml-1 text-[10px]">{d.unit}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
